import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { Search, MapPin, X, CalendarDays } from "lucide-react";
import { formatDate, eventPriceRange } from "@/lib/format";

const EVENT_TYPES = ["concert", "festival", "showcase", "listening"];

export default function EventsDirectory() {
    const [params, setParams] = useSearchParams();
    const [events, setEvents] = useState([]);
    const [meta, setMeta] = useState({ cities: [], countries: [], genres: [] });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState(params.get("q") || "");

    const filters = useMemo(
        () => ({
            q: params.get("q") || undefined,
            city: params.get("city") || undefined,
            country: params.get("country") || undefined,
            event_type: params.get("event_type") || undefined,
            price: params.get("price") || undefined,
            max_price: params.get("max_price") || undefined,
        }),
        [params],
    );

    useEffect(() => {
        endpoints.meta().then(setMeta).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        endpoints
            .listEvents(filters)
            .then((d) => {
                setEvents(d.events);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [filters]);

    const setFilter = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value);
        else next.delete(key);
        setParams(next, { replace: true });
    };

    const clearAll = () => setParams({}, { replace: true });

    return (
        <div data-testid="events-directory-page" className="pm-fade">
            <section className="border-b border-[#2a2a2a]">
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">
                        Directory
                    </div>
                    <h1 className="mt-2 font-display font-black uppercase tracking-tighter text-4xl md:text-6xl">
                        Events
                    </h1>
                    <p className="mt-4 text-[#a3a3a3] max-w-xl">
                        Free, paid, VIP, pay-at-door. Register your interest and turn up — payments happen offline.
                    </p>
                </div>
            </section>

            <section className="border-b border-[#2a2a2a] sticky top-16 z-30 bg-[#0a0a0a]">
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-4 flex flex-col md:flex-row gap-3 md:items-center flex-wrap">
                    <div className="flex items-center gap-2 border border-[#2a2a2a] px-3 py-2 flex-1 min-w-[200px]">
                        <Search className="w-4 h-4 text-[#666]" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && setFilter("q", q)}
                            onBlur={() => setFilter("q", q)}
                            placeholder="Search events…"
                            className="bg-transparent outline-none flex-1 text-sm placeholder:text-[#666]"
                            data-testid="events-search-input"
                        />
                    </div>
                    <Select label="City" value={filters.city || ""} options={meta.cities} onChange={(v) => setFilter("city", v)} testId="events-filter-city" />
                    <Select label="Country" value={filters.country || ""} options={meta.countries} onChange={(v) => setFilter("country", v)} testId="events-filter-country" />
                    <Select label="Type" value={filters.event_type || ""} options={EVENT_TYPES} onChange={(v) => setFilter("event_type", v)} testId="events-filter-type" />
                    <Select label="Price" value={filters.price || ""} options={["free", "paid"]} onChange={(v) => setFilter("price", v)} testId="events-filter-price" />
                    <input
                        type="number"
                        placeholder="Max $"
                        value={params.get("max_price") || ""}
                        onChange={(e) => setFilter("max_price", e.target.value)}
                        className="bg-transparent border border-[#2a2a2a] px-3 py-2 text-sm font-mono w-24 hover:border-[#f4f4f0] focus:border-[#ff4400] outline-none"
                        data-testid="events-filter-max-price"
                    />
                    <button onClick={clearAll} className="pm-btn" data-testid="events-clear-filters">
                        <X className="w-3 h-3 mr-1" /> Clear
                    </button>
                </div>
            </section>

            <section>
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-8">
                    <div className="text-xs font-mono uppercase tracking-widest text-[#666] mb-4" data-testid="events-count">
                        {loading ? "Loading…" : `${events.length} events`}
                    </div>
                    {!loading && events.length === 0 && (
                        <div className="border border-[#2a2a2a] p-10 text-center text-[#a3a3a3]" data-testid="events-empty">
                            No events match those filters.
                        </div>
                    )}
                    <div className="border border-[#2a2a2a]">
                        {events.map((e) => (
                            <Link
                                key={e.id}
                                to={`/events/${e.id}`}
                                data-testid={`directory-event-${e.id}`}
                                className="group flex flex-col md:flex-row items-stretch border-b border-[#2a2a2a] last:border-b-0 hover:bg-[#141414] transition-colors"
                            >
                                <div className="relative w-full md:w-64 aspect-[16/9] md:aspect-auto md:h-40 overflow-hidden shrink-0">
                                    <img src={e.image_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                                </div>
                                <div className="flex-1 p-5 flex flex-col md:flex-row md:items-center gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">
                                            {e.event_type} · {e.artist_name}
                                        </div>
                                        <div className="mt-1 font-display font-black uppercase text-xl md:text-2xl leading-tight group-hover:text-[#ff4400] transition-colors">
                                            {e.name}
                                        </div>
                                        <div className="mt-2 text-xs text-[#a3a3a3] flex flex-wrap gap-3">
                                            <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {formatDate(e.date)} · {e.start_time}</span>
                                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.venue_name} · {e.city}, {e.country}</span>
                                        </div>
                                    </div>
                                    <div className="flex md:flex-col md:items-end gap-4 md:gap-1">
                                        <div className="font-display font-black text-lg">{eventPriceRange(e)}</div>
                                        <div className="text-[10px] font-mono uppercase text-[#666]">
                                            {e.is_free ? "Free entry" : e.is_pay_at_door ? "Pay at door" : "Advance"}
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

function Select({ label, value, options, onChange, testId }) {
    return (
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="bg-transparent border border-[#2a2a2a] px-3 py-2 text-sm font-mono uppercase text-[#f4f4f0] hover:border-[#f4f4f0] focus:border-[#ff4400] outline-none"
        >
            <option value="" className="bg-[#141414]">{label}: All</option>
            {options.map((o) => (
                <option key={o} value={o} className="bg-[#141414]">{o}</option>
            ))}
        </select>
    );
}
